import { ReactNode } from 'react';
import {
  CircleCheck,
  CircleX,
  Clock,
  Loader2,
  RefreshCw,
  Smartphone,
  TimerOff,
  WifiOff,
  XCircle,
} from 'lucide-react';
import { PaymentStatus } from '../types';

/**
 * ─── PaymentStatusView ───────────────────────────────────────────────────────
 *
 * Rendu unifié de TOUS les états UI d'un paiement :
 * Loading · Pending · Verifying · Success · Failed · Cancelled · Expired ·
 * Network Error — avec les actions contextuelles (Retry, Re-vérifier, etc.).
 */

interface StatusAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  icon?: ReactNode;
}

interface PaymentStatusViewProps {
  status: PaymentStatus;
  /** Message additionnel (raison d'échec, instructions push…). */
  detail?: string | null;
  actions?: StatusAction[];
}

const VISUALS: Record<
  PaymentStatus,
  { icon: ReactNode; title: string; description: string; tone: string }
> = {
  [PaymentStatus.IDLE]: {
    icon: <Clock className="w-8 h-8 text-white" />,
    title: 'Prêt',
    description: 'Choisissez un moyen de paiement pour continuer.',
    tone: 'from-slate-500 to-slate-600',
  },
  [PaymentStatus.LOADING]: {
    icon: <Loader2 className="w-8 h-8 text-white animate-spin" />,
    title: 'Préparation du paiement…',
    description: 'Nous préparons votre transaction sécurisée.',
    tone: 'from-green-500 to-emerald-600',
  },
  [PaymentStatus.REDIRECTING]: {
    icon: <Loader2 className="w-8 h-8 text-white animate-spin" />,
    title: 'Redirection…',
    description: 'Vous allez être redirigé vers la page de paiement sécurisée.',
    tone: 'from-green-500 to-emerald-600',
  },
  [PaymentStatus.PENDING]: {
    icon: <Smartphone className="w-8 h-8 text-white" />,
    title: 'En attente de validation',
    description:
      'Une demande de paiement a été envoyée sur votre téléphone. Validez-la avec votre code PIN.',
    tone: 'from-amber-500 to-orange-600',
  },
  [PaymentStatus.VERIFYING]: {
    icon: <Loader2 className="w-8 h-8 text-white animate-spin" />,
    title: 'Vérification du paiement…',
    description:
      'Nous confirmons votre transaction auprès de l’opérateur. Cela peut prendre quelques secondes — ne fermez pas cette page.',
    tone: 'from-green-500 to-emerald-600',
  },
  [PaymentStatus.SUCCESS]: {
    icon: <CircleCheck className="w-8 h-8 text-white" />,
    title: 'Paiement réussi 🎉',
    description: 'Votre abonnement est actif. Bonne préparation !',
    tone: 'from-green-500 to-emerald-600',
  },
  [PaymentStatus.FAILED]: {
    icon: <CircleX className="w-8 h-8 text-white" />,
    title: 'Paiement échoué',
    description:
      'Votre paiement n’a pas abouti. Aucun montant ne vous a été débité.',
    tone: 'from-red-500 to-rose-600',
  },
  [PaymentStatus.CANCELLED]: {
    icon: <XCircle className="w-8 h-8 text-white" />,
    title: 'Paiement annulé',
    description: 'Vous avez annulé la transaction. Aucun montant débité.',
    tone: 'from-slate-500 to-slate-600',
  },
  [PaymentStatus.EXPIRED]: {
    icon: <TimerOff className="w-8 h-8 text-white" />,
    title: 'Session expirée',
    description:
      'La vérification n’a pas abouti dans les délais. Si vous avez été débité, votre abonnement sera activé automatiquement.',
    tone: 'from-amber-500 to-orange-600',
  },
  [PaymentStatus.NETWORK_ERROR]: {
    icon: <WifiOff className="w-8 h-8 text-white" />,
    title: 'Problème de connexion',
    description:
      'Impossible de joindre le service de paiement. Vérifiez votre connexion internet puis réessayez.',
    tone: 'from-red-500 to-rose-600',
  },
};

export function PaymentStatusView({ status, detail, actions = [] }: PaymentStatusViewProps) {
  const visual = VISUALS[status];

  return (
    <div className="w-full max-w-md mx-auto bg-overlay-1 border border-line rounded-2xl p-8 text-center">
      <div
        className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-gradient-to-br ${visual.tone} shadow-lg`}
      >
        {visual.icon}
      </div>
      <h1 className="text-xl font-bold text-fg mb-2">{visual.title}</h1>
      <p className="text-sm text-fg-subtle leading-relaxed">{visual.description}</p>
      {detail && (
        <p className="text-xs text-fg-faint leading-relaxed mt-3 bg-overlay-2 border border-line rounded-xl px-4 py-3">
          {detail}
        </p>
      )}

      {actions.length > 0 && (
        <div className="mt-6 space-y-2">
          {actions.map((action) => (
            <button
              key={action.label}
              onClick={action.onClick}
              className={
                action.variant === 'secondary'
                  ? 'w-full py-3 bg-overlay-2 border border-line rounded-xl text-sm text-fg-muted hover:bg-overlay-2/80 transition-colors flex items-center justify-center gap-2'
                  : 'w-full py-3.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-semibold active:scale-[0.98] transition-all flex items-center justify-center gap-2'
              }
            >
              {action.icon ?? (action.label.toLowerCase().includes('réessayer') ? <RefreshCw className="w-4 h-4" /> : null)}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
